import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { ApiError, ProblemType, type IEtagged } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type { IRoleDetail, IRoleEvent, IRoleSnapshot } from '../../api/role-contracts';
import { rolesApi } from '../../api/roles-api';
import { absent } from '../../components/account-facts';
import { usePermissionCatalogue, useUsersAndRolesTabs } from '../../components/account-roles';
import {
  AdminPage,
  Fact,
  LoadError,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { BackToList, searchFrom } from '../../components/participant-link';
import { TEAM_PATHS, userPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { NOT_REREAD } from '../person-hub/said';
import {
  CatalogueGate,
  ProblemLines,
  RoleFields,
  roleBody,
  roleRefusal,
  sameDraft,
  type IRoleDraft,
  type RoleProblems
} from './role-form';

/**
 * One Team Portal role beside the role list (SR-228, the role-model UI design's §7): what it is,
 * its permissions to edit, who holds it, its deletion and its history — `GET`, `PUT`, `DELETE`
 * `/admin/roles/{roleId}` and `…/history`, all `roles.manage`.
 *
 * **Every write is survey-api's to judge and re-reads the role.** An edit and a delete go under
 * `If-Match`, the detail's `ETag` quoted back as read (§9, 2) — a read without one turns both
 * off, never sends without it. A 409 `revision-conflict` keeps the draft and offers Reload, which
 * drops it for the role as saved (confirmed while there is a draft to drop); `permission-not-held`
 * and `last-role-manager` are said in survey-api's words; a 404 says the role is gone and re-reads.
 * A permission the editor does not hold is offered like any other: D5 is not computed here.
 *
 * **A role is remounted per id**, the refresh run's and the hub's rule: the split view keeps the
 * route's element mounted when the list opens another role, and a draft must not follow the press.
 */
export function AdminRolePage() {
  const { roleId = '' } = useParams();
  return <RolePane key={roleId} roleId={roleId} />;
}

/** The read's answer — `found` null for no such role. */
interface IRead {
  found: Nullable<IEtagged<IRoleDetail>>;
}

function RolePane({ roleId }: { roleId: string }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const tabs = useUsersAndRolesTabs();
  const rereadList = useListReread();
  const [written, setWritten] = React.useState(0);
  const { data, error, reload } = useAdminData<IRead>(
    async (signal) => {
      try {
        return { found: await rolesApi.role(roleId, signal) };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read this role'));
      }
    },
    [roleId]
  );
  const role = data?.found?.body ?? null;
  const etag = data?.found?.etag ?? null;

  /** After a write landed, or one whose answer never came: the role, its history, the list. */
  const reread = async () => {
    setWritten((count) => count + 1);
    rereadList();
    return reload();
  };

  return (
    <AdminPage title={role?.name ?? 'Role'} crumb="Users & roles" tabs={tabs}>
      <BackToList list={TEAM_PATHS.roles} search={searchFrom(state)} all="All roles" />

      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh — showing the role as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}

      {data === null ? (
        error ? null : <TableSkeleton rows={4} />
      ) : role ? (
        <>
          <RoleFacts role={role} />
          <RoleEditCard role={role} etag={etag} onReread={reread} />
          <RoleHolders role={role} />
          <RoleHistory roleId={role.roleId} written={written} />
          <RoleDeleteCard
            role={role}
            etag={etag}
            onDeleted={() => {
              rereadList();
              navigate(TEAM_PATHS.roles, { state: { deletedRole: role.name } });
            }}
            onReread={reread}
          />
        </>
      ) : (
        <Alert>
          <AlertTitle>No such role</AlertTitle>
          <AlertDescription>
            There is no Team Portal role by this id — it may have been deleted.
          </AlertDescription>
        </Alert>
      )}
    </AdminPage>
  );
}

function RoleFacts({ role }: { role: IRoleDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Role</CardTitle>
        <CardDescription>What is stored about this role.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
        <Fact label="Name" value={role.name} />
        <Fact
          label="Code"
          value={
            <span className="grid gap-0.5">
              <code>{role.code}</code>
              <span className="text-xs text-muted-foreground">
                Fixed when the role was made — migrations name roles by it.
              </span>
            </span>
          }
        />
        <Fact label="Description" value={role.description ?? absent('None')} />
        <Fact label="Holders" value={role.holderCount} />
        <Fact label="Permissions" value={role.permissions.length} />
      </CardContent>
    </Card>
  );
}

function draftOf(role: IRoleDetail): IRoleDraft {
  return {
    code: role.code,
    name: role.name,
    description: role.description ?? '',
    permissions: role.permissions
  };
}

/**
 * The role's name, description and permissions, Save and Cancel. `draft` is null while nothing
 * was changed — the read is drawn — so a re-read moves the form unless somebody is editing it.
 *
 * **A save is sent under the revision the draft started from** (`draftEtag`), not the latest
 * read's: a re-read under an open draft — a refused delete reads the role again — must not let
 * the draft land over a revision its author never saw. survey-api answers 409 instead, and the
 * draft is kept with Reload.
 *
 * **A landing keeps the values sent on screen until a re-read lands** (`sent`, dropped when the
 * read's role changes), so a re-read that fails draws neither the role as it was before the
 * press nor Save beside "Saved." — it says the page could not be read again.
 */
function RoleEditCard({
  role,
  etag,
  onReread
}: {
  role: IRoleDetail;
  etag: Nullable<string>;
  onReread: () => Promise<boolean>;
}) {
  const catalogue = usePermissionCatalogue(true);
  const [draft, setDraft] = React.useState<Nullable<IRoleDraft>>(null);
  const [draftEtag, setDraftEtag] = React.useState<Nullable<string>>(null);
  const [sent, setSent] = React.useState<Nullable<IRoleDraft>>(null);
  const [problems, setProblems] = React.useState<RoleProblems>({});
  const [conflict, setConflict] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState<Nullable<string>>(null);
  const stored = sent ?? draftOf(role);
  const shown = draft ?? stored;
  const changed = draft !== null && !sameDraft(draft, stored);
  const revision = draftEtag ?? etag;

  // A read that lands is the new baseline: what was sent is now what the read says.
  React.useEffect(() => setSent(null), [role]);

  const edit = (next: IRoleDraft) => {
    setSaved(null);
    if (draft === null) setDraftEtag(etag);
    setDraft(next);
  };

  const discard = () => {
    setDraft(null);
    setDraftEtag(null);
    setProblems({});
    setConflict(false);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!changed || revision === null) return;
    setSaving(true);
    setProblems({});
    setSaved(null);
    try {
      await rolesApi.updateRole(role.roleId, roleBody(shown), revision);
      setSent(shown);
      discard();
      const reread = await onReread();
      setSaved(reread ? 'Saved.' : `Saved.${NOT_REREAD}`);
    } catch (saveError) {
      const refusal = roleRefusal(saveError, 'saving the role');
      setProblems(refusal.problems);
      setConflict(refusal.conflict === true);
      if (refusal.reread) {
        void catalogue.reload();
        await onReread();
      }
    } finally {
      setSaving(false);
    }
  };

  /** Reload after a conflict: the role as saved, the draft dropped. */
  const reloadRole = async () => {
    discard();
    await onReread();
  };

  return (
    <Card data-role-edit>
      <CardHeader>
        <CardTitle>Name, description and permissions</CardTitle>
        <CardDescription>
          A change reaches every holder on their next request — nobody is signed out.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={(event) => void save(event)} noValidate>
          <CatalogueGate catalogue={catalogue}>
            {(permissions) => (
              <RoleFields
                draft={shown}
                onDraft={edit}
                catalogue={permissions}
                problems={problems}
                codeEditable={false}
                disabled={saving}
              />
            )}
          </CatalogueGate>
          {etag === null && (
            <p className="text-sm text-muted-foreground">
              This role came without its revision, so a change cannot be sent
              safely. Read the page again.
            </p>
          )}
          {saved && !changed && (
            <p role="status" className="text-sm">
              {saved}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              disabled={
                !changed || saving || revision === null || catalogue.data?.permissions == null
              }
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={draft === null || saving}
              onClick={discard}
            >
              Cancel
            </Button>
            {conflict && <ReloadButton dirty={changed} onReload={() => void reloadRole()} />}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** Reload after a conflict — a confirmed press while there is a draft it would drop. */
function ReloadButton({ dirty, onReload }: { dirty: boolean; onReload: () => void }) {
  if (!dirty) {
    return (
      <Button type="button" variant="outline" onClick={onReload}>
        Reload
      </Button>
    );
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="outline">
          Reload
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Drop your changes?</AlertDialogTitle>
          <AlertDialogDescription>
            The role is read again as it was saved, and what you changed here is lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={onReload}>Drop and reload</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Who holds the role, each to their user card — where a role is given and taken away. */
function RoleHolders({ role }: { role: IRoleDetail }) {
  return (
    <Card data-role-holders>
      <CardHeader>
        <CardTitle>Holders</CardTitle>
        <CardDescription>
          A role is given and taken away on each account’s user card.
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        {role.holders.length === 0 ? (
          absent('No account holds this role.')
        ) : (
          <ul className="grid gap-1">
            {role.holders.map((holder) => (
              <li key={holder.accountId}>
                <Link to={userPath(holder.email)} className="underline-offset-2 hover:underline">
                  {holder.email}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * `DELETE` under `If-Match`, a confirmed press — nothing undoes it. survey-api refuses it 409
 * `role-in-use` while any account holds the role, said with the count its problem carries
 * (`holderCount`) — never the count read before the press — and the role is read again for who
 * they are now. A 409 `revision-conflict` re-reads too: the card holds no draft to keep, and the
 * revision it would send again is the stale one. A seeded role is deleted like any other
 * (survey-api's D6).
 */
function RoleDeleteCard({
  role,
  etag,
  onDeleted,
  onReread
}: {
  role: IRoleDetail;
  etag: Nullable<string>;
  onDeleted: () => void;
  onReread: () => Promise<boolean>;
}) {
  const [deleting, setDeleting] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string[]>>(null);

  const remove = async () => {
    if (etag === null) return;
    setDeleting(true);
    setSaid(null);
    try {
      await rolesApi.deleteRole(role.roleId, etag);
      onDeleted();
    } catch (deleteError) {
      if (deleteError instanceof ApiError && deleteError.isProblem(ProblemType.revisionConflict)) {
        setSaid([
          'The role changed since you read it — it has been read again; check it and delete again.'
        ]);
        await onReread();
        return;
      }
      const refusal = roleRefusal(deleteError, 'deleting the role');
      const inUse =
        deleteError instanceof ApiError && deleteError.isProblem(ProblemType.roleInUse);
      const holders = inUse ? deleteError.problem?.holderCount : undefined;
      const lines = Object.values(refusal.problems).flat();
      setSaid(
        typeof holders === 'number' && holders > 0
          ? [
              ...lines,
              `${holders} ${holders === 1 ? 'account holds' : 'accounts hold'} it — take it ` +
                'from each on their user card first.'
            ]
          : lines
      );
      if (refusal.reread || inUse) await onReread();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card data-role-delete>
      <CardHeader>
        <CardTitle>Delete this role</CardTitle>
        <CardDescription>
          A role cannot be deleted while any account holds it. Its history is kept.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid justify-items-start gap-2">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="destructive" disabled={deleting || etag === null}>
              {deleting ? 'Deleting…' : 'Delete the role'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {role.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                Nothing undoes this. A role with the same code and name can be made again, but not
                this one.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={() => void remove()}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <ProblemLines lines={said ?? undefined} />
      </CardContent>
    </Card>
  );
}

const EVENT_WORDS: Record<string, string> = {
  created: 'Created',
  updated: 'Changed',
  deleted: 'Deleted'
};

/**
 * The role's `role_event` rows, newest first, all of them — survey-api does not page a role's
 * history — read on opening and after every write (`written`). What changed is drawn from
 * `detail` as sent: each member whose `before` and `after` differ, and a permission set's codes
 * added and removed; a row without one draws what it carries.
 */
function RoleHistory({ roleId, written }: { roleId: string; written: number }) {
  const { data, error } = useAdminData<IRoleEvent[]>(
    async (signal) => {
      try {
        return await rolesApi.history(roleId, signal);
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the role’s history'));
      }
    },
    [roleId, written]
  );

  return (
    <Card data-role-history>
      <CardHeader>
        <CardTitle>History</CardTitle>
        <CardDescription>Every change made to this role, newest first.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        {error && <LoadError message={error} />}
        {data === null ? (
          error ? null : (
            <p className="text-muted-foreground">Reading the history…</p>
          )
        ) : data.length === 0 ? (
          absent('Nothing recorded yet.')
        ) : (
          <ol className="grid gap-3">
            {data.map((event) => (
              <li key={event.eventId} className="grid gap-1" data-role-event={event.eventType}>
                <span>
                  <span className="font-medium">
                    {EVENT_WORDS[event.eventType] ?? event.eventType}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    {formatDateTime(event.occurredAt)} · {actorWords(event)}
                  </span>
                </span>
                <EventChanges event={event} />
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/** Who acted: the address as sent, the role beside it as stored (`admin` stays on old rows). A
 *  null actor is a row with none or an account since removed — survey-api sends the two alike. */
function actorWords(event: IRoleEvent): string {
  const who = event.actor?.email ?? 'the system, or an account since removed';
  return event.actorRole ? `${who} (${event.actorRole})` : who;
}

function EventChanges({ event }: { event: IRoleEvent }) {
  const before = event.detail?.before ?? null;
  const after = event.detail?.after ?? null;
  const lines: React.ReactNode[] = [];

  if (before && after) {
    if (before.name !== after.name && after.name !== undefined) {
      lines.push(
        <>
          Name: {before.name ?? '—'} → {after.name}
        </>
      );
    }
    if ((before.description ?? null) !== (after.description ?? null)) {
      lines.push(
        <>
          Description: {before.description ?? 'none'} → {after.description ?? 'none'}
        </>
      );
    }
    const was = before.permissions ?? [];
    const now = after.permissions ?? [];
    const added = now.filter((code) => !was.includes(code));
    const removed = was.filter((code) => !now.includes(code));
    if (added.length > 0) lines.push(<Codes label="Permissions added" codes={added} />);
    if (removed.length > 0) lines.push(<Codes label="Permissions removed" codes={removed} />);
  } else {
    const one: Nullable<IRoleSnapshot> = after ?? before;
    if (one?.name !== undefined) lines.push(<>Name: {one.name}</>);
    if (one?.permissions) lines.push(<Codes label="Permissions" codes={one.permissions} />);
  }

  if (lines.length === 0) return null;
  return (
    <ul className="grid gap-0.5 text-muted-foreground">
      {lines.map((line, index) => (
        <li key={index}>{line}</li>
      ))}
    </ul>
  );
}

function Codes({ label, codes }: { label: string; codes: readonly string[] }) {
  return (
    <>
      {label}:{' '}
      {codes.length === 0
        ? 'none'
        : codes.map((code, index) => (
            <React.Fragment key={code}>
              {index > 0 && ', '}
              <code className="text-xs">{code}</code>
            </React.Fragment>
          ))}
    </>
  );
}
